/* PopUpRenderer.js */

"use strict";
const _ = require("lodash");

class PopUpRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("pop-up-gl");
        this._rootElem.setAttribute("tabindex", "-1");
        if (!this._model.visible) {
            this._rootElem.classList.add("hidden");
        }
        if (this._model.content) {
            this._setContent();
            if (this._model.content.parentNode) {
                this._model.content.remove();
            } else {
                if (!this._model.parent) {
                    this._model.parent = document.querySelector("body");
                }
            }
        }
        if (this._model.anchor) {
            this._model.anchor.setAttribute("tabindex", "-1");
        }
        this._onContentBlur = _.debounce(
            (this._onDebouncedContentBlur = this._onDebouncedContentBlur.bind(this)),
            0
        );
        this._rootElem.component = this._model;
        this._anchorFocused = false;
        this._contentFocused = false;
        this._registerListeners();
        this.render();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("Anchor", (this._onAnchor = this._onAnchor.bind(this)));
        this._model.on("Parent", (this._onParent = this._onParent.bind(this)));
        this._model.on("AnchorPoint", (this._onAnchorPoint = this._onAnchorPoint.bind(this)));
        this._model.on(
            "DisplayDirection",
            (this._onDisplayDirection = this._onDisplayDirection.bind(this))
        );
        this._model.on("Content", (this._onContent = this._onContent.bind(this)));
    }

    _unregisterListeners() {
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("Anchor", this._onAnchor);
        this._model.removeListener("Parent", this._onParent);
        this._model.removeListener("AnchorPoint", this._onAnchorPoint);
        this._model.removeListener("DisplayDirection", this._onDisplayDirection);
        this._model.removeListener("Content", this._onContent);
        if (this._model._openOnHover) {
            this._model.anchor.removeEventListener("mouseover", this._onAnchorMouseDown);
        } else {
            this._model.anchor.removeEventListener("mousedown", this._onAnchorMouseDown);
        }
    }

    _showPopUp() {
        if (!this._rootElem.parentNode) {
            this._renderContent();

            if (
                this._rootElem.offsetTop + this._rootElem.offsetHeight >
                document.querySelector("body").offsetHeight
            ) {
                if (this._rootElem.querySelector(".arrow")) {
                    this._rootElem.style.top = `${
                        this._rootElem.offsetTop - this._rootElem.offsetHeight + 10
                    }px`;

                    this._rootElem.querySelector(".arrow").classList.add("pull-down-arrow");
                }
            }

            this._rootElem.addEventListener(
                "blur",
                (this._onContentBlur = this._onContentBlur.bind(this))
            );
            setTimeout((this._focusContent = this._focusContent.bind(this)), 0);
            this._model.visible = true;
            this._model.emit("PopUpVisible");
        }
    }

    _focusContent() {
        this._rootElem.focus();
    }

    _hidePopUp() {
        if (this._rootElem.parentNode) {
            this._removeContent();
            this._rootElem.removeEventListener("blur", this._onContentBlur);
            if (this._activeElement) {
                this._activeElement.removeEventListener(
                    "blur",
                    (this._onContentBlur = this._onContentBlur.bind(this))
                );
            }
            this._model.visible = false;
            this._model.emit("PopUpHidden");
        }
    }

    _onVisible() {
        if (this._model.visible) {
            this._showPopUp();
        } else {
            this._hidePopUp();
        }
    }

    _onDebouncedContentBlur() {
        this._activeElement = document.activeElement;
        if (this._rootElem.contains(this._activeElement)) {
            if (this._activeElement) {
                this._activeElement.removeEventListener(
                    "blur",
                    (this._onContentBlur = this._onContentBlur.bind(this))
                );
            }
            this._activeElement.addEventListener(
                "blur",
                (this._onContentBlur = this._onContentBlur.bind(this))
            );
            this._contentFocused = true;
        } else {
            this._hidePopUp();
            this._activeElement.removeEventListener("blur", this._onContentBlur);
        }
    }

    async _onAnchorMouseDown() {
        if (this._model.visible && !this._model._allowClickInside && !this._model._openOnHover) {
            this._hidePopUp();
        } else {
            let proceed = true;
            if (this._model.confirmPopupFunc) {
                proceed = await this._model.confirmPopupFunc();
            }
            if (proceed) {
                this._showPopUp();
            }
        }
    }

    // keeping this around for when we need mouse hover logic
    _mouseInBounds(evt) {
        let inBounds = false;
        if (this._inAnchorBounds(evt.x, evt.y) || this._inPopUpBounds(evt.x, evt.y)) {
            inBounds = true;
        }
        return inBounds;
    }

    _inAnchorBounds(x, y) {
        let inAnchorBounds = this._inRect(this._anchorRect, x, y);
        return inAnchorBounds;
    }

    _inPopUpBounds(x, y) {
        this._rootElemRect = this._rootElem.getBoundingClientRect();
        let inPopUpBounds = this._inRect(this._rootElemRect, x, y);
        return inPopUpBounds;
    }

    _inRect(rect, x, y) {
        let inBounds = false;
        if (x > rect.left && x < rect.right && y > rect.top && y < rect.bottom) {
            inBounds = true;
        }
        return inBounds;
    }

    _onContent() {
        this._setContent();
    }

    _onDisplayDirection() {
        if (this._model.visible) {
            this._hidePopUp();
            this._showPopUp();
        }
    }

    _onAnchorPoint() {
        if (this._model.visible) {
            this._hidePopUp();
            this._showPopUp();
        }
    }

    _onAnchor() {
        if (this._model.anchor) {
            this._model.anchor.setAttribute("tabindex", "-1");
        }
        if (this._model.visible) {
            this._hidePopUp();
            this._showPopUp();
        }
    }

    _onParent() {
        if (this._model.visible) {
            this._hidePopUp();
            this._showPopUp();
        }
    }

    _setContent() {
        if (this._model.content instanceof HTMLElement) {
            this._rootElem.appendChild(this._model.content);
        } else if (_.isString(this._model.content)) {
            this._rootElem.innerHTML = this._model.content;
        }
    }

    _renderContent() {
        if (!this._rootElem.parentNode) {
            if (this._model.content) {
                this._setContent();
                this._setAnchorPosition();
                this._model.parent.appendChild(this._rootElem);
                this._rootElem.classList.remove("hidden");
            }
        }
    }

    _removeContent() {
        if (this._model.parent.contains(this._rootElem)) {
            this._rootElem.classList.add("hidden");
            this._rootElem.remove();
        }
    }

    _getPopUpRect() {
        let tmpRootElem = this._rootElem.cloneNode(true);
        tmpRootElem.classList.remove("hidden");
        tmpRootElem.style.position = "fixed";
        tmpRootElem.style.visibility = "hidden";
        this._model.parent.appendChild(tmpRootElem);
        let contentRect = tmpRootElem.getBoundingClientRect();
        tmpRootElem.remove();
        return contentRect;
    }

    _setAnchorPosition() {
        this._anchorRect = this._model.anchor.getBoundingClientRect();
        let pos;
        switch (this._model.anchorPoint) {
            case "center":
                pos = this._getCenterAnchorPosition();
                break;
            // CLOCKWISE
            case "top":
                pos = this._getTopAnchorPosition();
                break;
            case "topRight":
                pos = this._getTopRightAnchorPosition();
                break;
            case "right":
                pos = this._getRightAnchorPosition();
                break;
            case "bottomRight":
                pos = this._getElementBottomRightPosition();
                break;
            case "bottom":
                pos = this._getBottomAnchorPosition();
                break;
            case "bottomLeft":
                pos = this._getElementBottomLeftPosition();
                break;
            case "left":
                pos = this._getLeftAnchorPosition();
                break;
            case "topLeft":
                pos = this._getElementTopLeftPosition();
                break;
        }
        if (pos) {
            pos = this._offsetDisplayDirection(pos);
            this._rootElem.style.top = pos.top + "px";
            this._rootElem.style.left = pos.left + "px";
        }
        return pos;
    }

    _offsetDisplayDirection(pos) {
        let popUpRect = this._getPopUpRect();
        switch (this._model.displayDirection) {
            case "top":
                pos.top = pos.top - popUpRect.height;
                pos.left = pos.left - popUpRect.width / 2;
                break;
            case "topRight":
                pos.top = pos.top - popUpRect.height;
                break;
            case "right":
                pos.top = pos.top - popUpRect.height / 2;
                break;
            case "bottomRight":
                // nothing to be done
                break;
            case "bottom":
                pos.left = pos.left - popUpRect.width / 2;
                break;
            case "bottomLeft":
                pos.left = pos.left - popUpRect.width;
                break;
            case "left":
                pos.top = pos.top - popUpRect.height / 2;
                pos.left = pos.left - popUpRect.width;
                break;
            case "topLeft":
                pos.top = pos.top - popUpRect.height;
                pos.left = pos.left - popUpRect.width;
                break;
        }
        return pos;
    }

    _getTopAnchorPosition() {
        let position = {};
        let rect = this._anchorRect;
        position.top = Math.floor(rect.top);
        position.left = Math.floor(rect.left + rect.width / 2);
        return position;
    }

    _getBottomAnchorPosition() {
        let position = {};
        let rect = this._anchorRect;
        position.top = Math.floor(rect.bottom);
        position.left = Math.floor(rect.left + rect.width / 2);
        return position;
    }

    _getCenterAnchorPosition() {
        let Position = {};
        let rect = this._anchorRect;
        Position.top = Math.floor(rect.top + rect.height / 2);
        Position.left = Math.floor(rect.left + rect.width / 2);
        return Position;
    }

    _getLeftAnchorPosition() {
        let Position = {};
        let rect = this._anchorRect;
        Position.top = Math.floor(rect.top + rect.height / 2);
        Position.left = Math.floor(rect.left);
        return Position;
    }

    _getRightAnchorPosition() {
        let Position = {};
        let rect = this._anchorRect;
        Position.top = Math.floor(rect.top + rect.height / 2);
        Position.left = Math.floor(rect.left + rect.width);
        return Position;
    }

    _getTopRightAnchorPosition() {
        let Position = {};
        let rect = this._anchorRect;
        Position.top = Math.floor(rect.top);
        Position.left = Math.floor(rect.right);
        return Position;
    }

    _getElementBottomRightPosition() {
        let Position = {};
        let rect = this._anchorRect;
        Position.top = Math.floor(rect.top + rect.height);
        Position.left = Math.floor(rect.left + rect.width);
        return Position;
    }

    _getElementBottomLeftPosition() {
        let Position = {};
        let rect = this._anchorRect;
        Position.top = Math.floor(rect.top + rect.height);
        Position.left = Math.floor(rect.left);
        return Position;
    }

    _getElementTopLeftPosition() {
        let Position = {};
        let rect = this._anchorRect;
        Position.top = Math.floor(rect.top);
        Position.left = Math.floor(rect.left);
        return Position;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        this._rootElem.removeEventListener("blur", this._onContentBlur);
        this._onContentBlur.cancel();
        return new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;

            resolve();
        });
    }
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return new Promise((resolve /*,reject*/) => {
            if (this._model.anchor) {
                if (this._model._openOnHover) {
                    this._model.anchor.addEventListener(
                        "mouseover",
                        (this._onAnchorMouseDown = this._onAnchorMouseDown.bind(this))
                    );
                } else {
                    this._model.anchor.addEventListener(
                        "mousedown",
                        (this._onAnchorMouseDown = this._onAnchorMouseDown.bind(this))
                    );
                }
            }
            resolve();
        });
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/
    //TODO Private Render Methods here
    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = PopUpRenderer;
